import './eligibility/eligibility-minor.scss';

import React from 'react';

import PhoneField from '@components/Form/PhoneField';
import { BIOLOGICAL_SEX_OPTIONS } from '@customTypes/constants';
import { type YesNoBoolean } from '@customTypes/form';
import { isMinorDOB } from '@utils/dates';
import { YES_NO_OPTIONS } from '@utils/forms';

import type { OrderingEligibilityFormikProps } from '../OrderCheckEligibility/OrderCheckEligibility.validation';
import InputField from './common/InputField';
import { RadioButtonField } from './common/RadioButtonField';
import SelectField from './common/SelectField';
import { GuardianFormFields } from './eligibility/GuardianFormFields';
import { MinorAgeAlert } from './eligibility/MinorAgeAlert';

interface EligibilityFormFieldsProps {
  allowsMinorWithGuardian?: boolean;
  formik: OrderingEligibilityFormikProps;
  onNotGuardianClick?: () => void;
  showPromoCodeField: boolean;
}

const BONE_MARROW_TOOLTIP =
  "A bone marrow transplant changes the DNA in some of the cells a cheek swab collects to match the donor's DNA. This can cause genetic testing results to be inaccurate or misleading.";

function subjectLabel(label: string, forMinor: boolean): string {
  return forMinor ? `Patient ${label.toLowerCase()}` : label;
}

export function EligibilityFormFields({
  allowsMinorWithGuardian = false,
  formik,
  onNotGuardianClick,
  showPromoCodeField,
}: EligibilityFormFieldsProps) {
  const showMinorFlow = allowsMinorWithGuardian && isMinorDOB(formik.values.dob);
  const boneMarrowLabel = showMinorFlow
    ? 'Has the patient ever had a bone marrow transplant?'
    : 'Have you ever had a bone marrow transplant?';

  return (
    <div className='form-grid'>
      <div className='field'>
        <InputField
          id='firstName'
          label={subjectLabel('First name', showMinorFlow)}
          required
          placeholder={subjectLabel('First name', showMinorFlow)}
          value={formik.values.firstName}
          onChange={val => formik.setFieldValue('firstName', val)}
          onBlur={() => formik.setFieldTouched('firstName', true)}
          showError={formik.touched.firstName}
          error={formik.errors.firstName}
          phiSanitize
        />
      </div>

      <div className='field'>
        <InputField
          id='lastName'
          label={subjectLabel('Last name', showMinorFlow)}
          required
          placeholder={subjectLabel('Last name', showMinorFlow)}
          value={formik.values.lastName}
          onChange={val => formik.setFieldValue('lastName', val)}
          onBlur={() => formik.setFieldTouched('lastName', true)}
          showError={formik.touched.lastName}
          error={formik.errors.lastName}
          phiSanitize
        />
      </div>

      <div className='field'>
        <InputField
          id='dob'
          label={subjectLabel('Date of birth', showMinorFlow)}
          required
          type='date'
          autoComplete='bday'
          value={formik.values.dob ?? ''}
          onChange={val => formik.setFieldValue('dob', val)}
          onBlur={() => formik.setFieldTouched('dob', true)}
          showError={formik.touched.dob}
          error={formik.errors.dob}
          phiHidden
        />
      </div>

      <div className='field'>
        <SelectField
          id='sexAtBirth'
          label={subjectLabel('Sex assigned at birth', showMinorFlow)}
          required
          value={formik.values.sexAtBirth}
          options={BIOLOGICAL_SEX_OPTIONS}
          onChange={val => formik.setFieldValue('sexAtBirth', val)}
          onBlur={() => formik.setFieldTouched('sexAtBirth', true)}
          showError={formik.touched.sexAtBirth}
          error={formik.errors.sexAtBirth}
          phiSanitize
        />
      </div>

      {/* Stays visible for minors but optional: identity comes from the invite link. */}
      <div className='field'>
        <InputField
          id='email'
          label={showMinorFlow ? 'Patient email (optional)' : 'Email'}
          required={!showMinorFlow}
          type='email'
          placeholder='name@example.com'
          value={formik.values.email ?? ''}
          onChange={val => formik.setFieldValue('email', val)}
          onBlur={() => formik.setFieldTouched('email', true)}
          showError={formik.touched.email}
          error={formik.errors.email}
          phiSanitize
        />
      </div>

      <div className='field'>
        <PhoneField
          name='phone'
          label={showMinorFlow ? 'Patient phone number (optional)' : 'Phone number'}
          required={!showMinorFlow}
          helperText='Phone number'
          phiSanitize
        />
      </div>

      <div className='field field-span-2'>
        <RadioButtonField<YesNoBoolean>
          id='boneMarrowTransplant'
          label={boneMarrowLabel}
          labelTooltip={BONE_MARROW_TOOLTIP}
          required
          value={formik.values.boneMarrowTransplant}
          options={YES_NO_OPTIONS}
          onChange={val => formik.setFieldValue('boneMarrowTransplant', val)}
          showError={!!(formik.touched.boneMarrowTransplant && formik.errors.boneMarrowTransplant)}
          error={formik.errors.boneMarrowTransplant as string}
        />
        {formik.values.boneMarrowTransplant === 'YES' && (
          <div className='warning-text' role='alert'>
            We cannot accept orders from individuals who have received transplants.
          </div>
        )}
      </div>

      {showMinorFlow ? (
        <div className='field field-span-2'>
          <MinorAgeAlert firstName={formik.values.firstName} dob={formik.values.dob} />
        </div>
      ) : null}

      {showMinorFlow && onNotGuardianClick ? (
        <GuardianFormFields
          formik={formik}
          patientFirstName={formik.values.firstName}
          onNotGuardianClick={onNotGuardianClick}
        />
      ) : null}

      {showPromoCodeField && (
        <div className='field'>
          <InputField
            id='promoCode'
            label='Promo code'
            value={formik.values.promoCode}
            placeholder='Enter promo code (optional)'
            onChange={val => formik.setFieldValue('promoCode', val)}
            onBlur={() => formik.setFieldTouched('promoCode', true)}
            showError={formik.touched.promoCode}
            error={formik.errors.promoCode}
          />
        </div>
      )}
    </div>
  );
}
